import { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import type { Audience } from '@epasu/core';
import { crm, type ContactRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OrganizationDialog } from '@/components/OrganizationDialog';
import { FirmenFeld } from '@/components/FirmenFeld';

interface Props {
  /** null = neuer Kontakt */
  contact: ContactRow | null;
  audiences: Audience[];
  /** Vorbelegung beim Anlegen aus einer Firmenakte heraus. */
  defaultOrganizationName?: string;
  defaultAudienceId?: string;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  audienceId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  salutation: string;
  /** '' heißt: nicht angegeben — dann wird nicht persönlich angesprochen. */
  formOfAddress: '' | 'herr' | 'frau';
  personalSalutation: string;
  organizationName: string;
  notes: string;
}

const leer = (audienceId: string): FormState => ({
  audienceId,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: '',
  salutation: '',
  formOfAddress: '',
  personalSalutation: '',
  organizationName: '',
  notes: '',
});

/**
 * Anlegen und Bearbeiten in einem Formular.
 *
 * Die Firma wird als Freitext mit Vorschlagsliste eingegeben, nicht als
 * Auswahlfeld: Beim Anlegen eines Kontakts existiert die Firma oft noch nicht,
 * und ein erzwungener Zwischenschritt „erst Firma anlegen“ hält nur auf. Der
 * Server legt sie bei Bedarf an und erkennt dabei Schreibvarianten.
 */
export function ContactDialog({
  contact,
  audiences,
  defaultOrganizationName,
  defaultAudienceId,
  onClose,
  onSaved,
}: Props) {
  const [form, setForm] = useState<FormState>(() =>
    contact
      ? {
          audienceId: contact.audienceId,
          firstName: contact.firstName ?? '',
          lastName: contact.lastName ?? '',
          email: contact.email ?? '',
          phone: contact.phone ?? '',
          role: contact.role ?? '',
          salutation: contact.salutation ?? '',
          formOfAddress: contact.formOfAddress ?? '',
          personalSalutation: contact.personalSalutation ?? '',
          organizationName: contact.organizationName ?? '',
          notes: contact.notes ?? '',
        }
      : {
          ...leer(defaultAudienceId ?? audiences[0]?.id ?? ''),
          organizationName: defaultOrganizationName ?? '',
        },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [neueFirma, setNeueFirma] = useState(false);
  const firstField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstField.current?.focus();
  }, []);

  useEffect(() => {
    // Liegt der Firmen-Dialog oben, gehört Escape ihm allein — sonst gingen
    // beide Fenster auf einen Tastendruck zu und die Eingaben wären weg.
    if (neueFirma) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, neueFirma, onClose]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.audienceId) {
      setError('Bitte eine Zielgruppe wählen.');
      return;
    }
    if (!form.firstName.trim() && !form.lastName.trim() && !form.email.trim()) {
      setError('Bitte mindestens einen Namen oder eine E-Mail-Adresse angeben.');
      return;
    }

    setBusy(true);
    setError(null);

    const payload = {
      audienceId: form.audienceId,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      role: form.role,
      salutation: form.salutation,
      formOfAddress: form.formOfAddress,
      personalSalutation: form.personalSalutation,
      notes: form.notes,
      organizationName: form.organizationName,
    };

    try {
      if (contact) {
        await crm.contacts.update(contact.id, payload);
      } else {
        await crm.contacts.create(payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={contact ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-auto w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">
          {contact ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}
        </h2>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Vorname">
            <Input
              ref={firstField}
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
            />
          </Field>
          <Field label="Nachname">
            <Input
              value={form.lastName}
              onChange={(e) => set('lastName', e.target.value)}
            />
          </Field>

          <Field label="E-Mail" className="sm:col-span-2">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="name@firma.de"
            />
          </Field>

          <Field label="Telefon">
            <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label="Position">
            <Input
              value={form.role}
              onChange={(e) => set('role', e.target.value)}
              placeholder="Schulleitung, Einkauf …"
            />
          </Field>

          {/*
            Kein Vorschlag aus dem Vornamen: bei Kim, Andrea oder Sascha läge er
            daneben, und die Mail ginge mit falscher Anrede raus.
          */}
          <Field label="Anredeform für {{anrede_persoenlich}}" className="sm:col-span-2">
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.formOfAddress}
              onChange={(e) => set('formOfAddress', e.target.value as FormState['formOfAddress'])}
            >
              <option value="">— nicht angegeben (Team-Anrede) —</option>
              <option value="frau">Frau</option>
              <option value="herr">Herr</option>
            </select>
          </Field>

          <Field label="Persönliche Anrede für {{anrede_persoenlich}}" className="sm:col-span-2">
            <Input
              value={form.personalSalutation}
              onChange={(e) => set('personalSalutation', e.target.value)}
              placeholder="z. B. Sehr geehrter Herr Rehn — leer: aus Anredeform und Nachname"
            />
          </Field>

          <Field label="Anrede für {{csv_anrede}}" className="sm:col-span-2">
            <Input
              value={form.salutation}
              onChange={(e) => set('salutation', e.target.value)}
              placeholder="z. B. Liebes Team des Neuen Gymnasiums — leer: wie {{anrede}}"
            />
          </Field>

          <Field label="Firma" className="sm:col-span-2">
            <div className="flex gap-2">
              <FirmenFeld
                className="flex-1"
                value={form.organizationName}
                onChange={(name) => set('organizationName', name)}
                audienceId={form.audienceId || undefined}
                placeholder="Firma suchen — neu, falls noch nicht vorhanden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setNeueFirma(true)}
                disabled={busy}
                title="Firma mit Anschrift, Domain und Branche anlegen"
              >
                <Plus className="mr-2 h-4 w-4" />
                Anlegen
              </Button>
            </div>
          </Field>

          <Field label="Zielgruppe" className="sm:col-span-2">
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.audienceId}
              onChange={(e) => set('audienceId', e.target.value)}
            >
              {audiences.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Notizen" className="sm:col-span-2">
            <textarea
              className="min-h-[5rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Gesprächsnotizen, Besonderheiten …"
            />
          </Field>
        </div>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Speichere …' : 'Speichern'}
          </Button>
        </div>
      </form>
    </div>

    {neueFirma && (
      <OrganizationDialog
        organization={null}
        audiences={audiences}
        // Die Firma erbt die Zielgruppe aus dem Kontaktformular — sonst
        // ließen sich die beiden gleich darauf nicht verbinden.
        lockedAudienceId={form.audienceId}
        onClose={() => setNeueFirma(false)}
        onSaved={(firma) => {
          setNeueFirma(false);
          // Der Name genügt: beim Speichern findet der Server die Firma
          // darüber wieder und legt keine zweite an.
          set('organizationName', firma.name);
        }}
      />
    )}
    </>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block space-y-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
